import { DSCircularButton } from "@components/design-system/molecules";
import { DSNoteInput } from "@components/design-system/organisms/DSNoteInput";
import { useFindAndReplace, usePlainNoteEditor } from "@hooks";
import { useCompanion } from "@hooks/use-companion";
import { useWritingAssistant } from "@hooks/use-writing-assistant";
import { toTitleCase } from "@src/utils";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useFormContext, useWatch } from "react-hook-form";

import { UserPermissions } from "../../../../hooks/use-premium-feature";
import Twemoji from "../../../../Twemoji";
import CascadingDropdown from "../../../common/menus/CascadingDropdown";
import PremiumFeatureGate from "../../../common/premium/PremiumFeatureGate";
import UpgradeModal from "../../../common/premium/UpgradeModal";
import CustomizationFeedback from "../../common/CustomizationFeedback";
import NoteInput from "../../legacy/LegacyNoteInput";

export default function CustomForm({ control, note }) {
  const {
    aiAssistantEnabled,
    languageAiAssistantEnabled,
    subheadingsAiAssistantEnabled,
    useLegacyNotes,
    writingCompanion,
  } = useFlags();
  const usePlainEditor = usePlainNoteEditor();
  const { selectFindAndReplaceText } = useFindAndReplace();

  if (useLegacyNotes || usePlainEditor) {
    return (
      <NoteInput
        control={control}
        fieldName="customText"
        label="Transcript"
        title={toTitleCase(note?.jobType || "Custom")}
        headerStyle="bg-gray-400 dark:bg-gray-600"
        boxStyle="border-gray-400 bg-gray-50 dark:bg-gray-700 dark:border-gray-600 border my-1 rounded-xl w-full text-xs md:text-sm align-top text-gray-700 dark:text-gray-300 divide-gray-400 dark:divide-gray-600"
        aiAssistantEnabled={aiAssistantEnabled}
        languageAiAssistantEnabled={languageAiAssistantEnabled}
        subheadingsAiAssistantEnabled={subheadingsAiAssistantEnabled}
        noteUuid={note?.uuid}
        onMouseUp={(e) => {
          selectFindAndReplaceText(e);
        }}
      />
    );
  }

  return (
    <CustomFormContent
      note={note}
      control={control}
      aiAssistantEnabled={aiAssistantEnabled}
      languageAiAssistantEnabled={languageAiAssistantEnabled}
      subheadingsAiAssistantEnabled={subheadingsAiAssistantEnabled}
      writingCompanion={writingCompanion}
      selectFindAndReplaceText={selectFindAndReplaceText}
    />
  );
}

function CustomFormContent({
  note,
  control,
  aiAssistantEnabled,
  languageAiAssistantEnabled,
  subheadingsAiAssistantEnabled,
  writingCompanion,
  selectFindAndReplaceText,
}) {
  const { setValue, getValues } = useFormContext();
  const { expandSidebar, createNewSession, creatingSession } =
    useCompanion();
  const customText = useWatch({ control, name: "customText" });

  const noteData = { content: customText || "" };

  const handleChange = (newData) => {
    if (noteData.content !== newData.content) {
      setValue("customText", newData.content, { shouldDirty: true });
    }
  };

  const wa = useWritingAssistant({
    noteUuid: note?.uuid,
    fieldName: "customText",
    getFieldValue: () => getValues("customText"),
    setFieldValue: (v) =>
      setValue("customText", v, { shouldDirty: true }),
    getOtherFieldValue: (fn) => getValues(fn),
    setOtherFieldValue: (fn, v) =>
      setValue(fn, v, { shouldDirty: true }),
    languageAiAssistantEnabled,
    subheadingsAiAssistantEnabled,
  });

  const label = toTitleCase(note?.jobType || "Custom");

  return (
    <>
      <UpgradeModal
        shouldShow={wa.showUpgradeModal}
        setShouldShow={wa.setShowUpgradeModal}
        featureName="Writing Assistant"
      />

      <DSNoteInput
        data={noteData}
        onChange={handleChange}
        onMouseUp={selectFindAndReplaceText}
        dataTestId="note-input"
      >
        <div>
          <DSNoteInput.NoteField
            section="freeform"
            label={label}
            showSparkleButton={false}
            showCopyButton
            actions={
              aiAssistantEnabled && !wa.diffState ? (
                <>
                  <PremiumFeatureGate
                    featureName="Writing Assistant"
                    requiredPermission={
                      UserPermissions.WRITING_ASSISTANT_ENABLED
                    }
                    iconPosition="center"
                    iconSize="xs"
                  >
                    <CascadingDropdown
                      options={wa.options}
                      setIsMenuOpen={wa.setIsMenuOpen}
                      title={label}
                      isTriggerComponentDisabled={
                        wa.loadingBeforeAndDuringStreaming
                      }
                      triggerComponent={({ onClick }) => (
                        <DSCircularButton
                          onClick={onClick}
                          size="sm"
                          disabled={
                            wa.loadingBeforeAndDuringStreaming
                          }
                          title="Trigger Writing Assistant"
                          dataTestId="cascading-dropdown-trigger"
                        >
                          <Twemoji
                            className="text-xl z-20"
                            emoji="✨"
                          />
                        </DSCircularButton>
                      )}
                    />
                  </PremiumFeatureGate>
                  {writingCompanion && (
                    <PremiumFeatureGate
                      featureName="Companion"
                      iconPosition="center"
                      iconSize="xs"
                    >
                      <DSCircularButton
                        size="sm"
                        disabled={creatingSession}
                        onClick={() => {
                          if (creatingSession) return;
                          expandSidebar();
                          createNewSession();
                        }}
                        title="Edit with Quill"
                        dataTestId="edit-with-quill-trigger"
                      >
                        <Twemoji
                          className="text-xl z-20"
                          emoji="🦔"
                        />
                      </DSCircularButton>
                    </PremiumFeatureGate>
                  )}
                </>
              ) : undefined
            }
            diffState={wa.diffState}
            onDiffAccept={wa.acceptChanges}
            onDiffReject={wa.rejectChanges}
          />

          {wa.isShowingExtraContent &&
            wa.customizationFeedback.hasFeedbackAccess && (
              <CustomizationFeedback
                llmCustomizationUuid={
                  wa.customizationFeedback.llmCustomizationUuid
                }
              />
            )}
        </div>
      </DSNoteInput>
    </>
  );
}
